import { useEffect } from 'react'
import { Alert, Form, Input, InputNumber, Modal, Select } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { todayInTenantZone } from '@/shared/lib/format'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import type { OrgNode } from '@/features/org/orgApi'
import { REASON_MAX } from './hrApi'
import { generatePayrollRun, type PayrollRun } from './payrollApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

interface Props {
  open: boolean
  /** ⚠️ FAQAT xarajat markazi bo'la oladigan tugunlar — pastdagi izoh */
  nodes: readonly OrgNode[]
  /** Ro'yxat ishonchlimi — yiqilgan so'rov «tugun yo'q» bo'lib ko'rinmasin */
  nodesReliable: boolean
  onClose: () => void
  onDone: (run: PayrollRun) => void
}

interface FormValues {
  orgNodeId: string
  year: number
  month: number
  note?: string
}

/**
 * Oylik vedomostni yig'ish.
 *
 * <h2>⚠️ FILIAL — XARAJAT MARKAZI BO'LISHI SHART</h2>
 * Bazadagi qorovul tugun turining `is_cost_center` IMKONIYATIGA
 * qaraydi, nomiga emas (§19.3). Panel ham shunday: ro'yxat imkoniyat
 * bo'yicha filtrlanadi va administrator yangi tur ochsa u darhol
 * ko'rinadi.
 *
 * <h2>⚠️ BITTA FILIAL + BITTA OY = BITTA VEDOMOST</h2>
 * Tabiiy kalit (`payroll_run_period_uq`) takrorlashni rad etadi va
 * server 409 bilan «uni oching yoki qayta yig'ing» deydi. Bu
 * idempotentlikning O'RNINI bosadi — alohida `Idempotency-Key` yo'q va
 * kerak ham emas.
 *
 * <h2>⚠️ SHOXLAR KESISHMAYDI</h2>
 * Bosh ofis uchun vedomost yig'ib, keyin uning ostidagi filial uchun
 * ham yig'ish xodimni IKKI MARTA hisoblardi. Buni ham baza to'sadi
 * (`hr.payroll_run_guard`) va xabar shu oynada ko'rinadi.
 *
 * <h2>⚠️ DAVR — IKKI SON, «2026-08» EMAS</h2>
 * Server `year` va `month` ni alohida oladi (`PayrollRunDraft`).
 * Standart qiymat TENANT zonasidagi joriy oy: `dayjs()` brauzer
 * zonasida ishlaydi va oy chegarasida oyni surib yuborardi.
 */
export function PayrollGenerateModal({ open, nodes, nodesReliable, onClose, onDone }: Props) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!open) return
    const today = todayInTenantZone()
    form.setFieldsValue({
      orgNodeId: undefined,
      year: Number(today.slice(0, 4)),
      month: Number(today.slice(5, 7)),
      note: '',
    })
  }, [open, form])

  const generate = useMutation({
    mutationFn: (values: FormValues) =>
      generatePayrollRun({
        orgNodeId: values.orgNodeId,
        year: values.year,
        month: values.month,
        note: values.note?.trim() ? values.note.trim() : null,
      }),
    onSuccess: (run) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.payrollRuns })
      form.resetFields()
      onDone(run)
      onClose()
    },
  })

  const close = () => {
    generate.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={t('hr.payroll.generateTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => generate.mutate(values))}
      okButtonProps={{ disabled: !nodesReliable || nodes.length === 0 }}
      confirmLoading={generate.isPending}
      okText={t('hr.payroll.generateAction')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={560}
    >
      <div style={{ marginBottom: 16 }}>
        <ExpandableHint text={t('hr.payroll.generateNote')} fontSize={13} />
      </div>

      {generate.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(generate.error, t)}
        />
      ) : null}

      {/* ⚠️ «Tugun yo'q» xulosasi FAQAT ishonchli ro'yxatda (`guards.mjs`
          3-bo'limi): yiqilgan so'rov ham bo'sh ro'yxat beradi */}
      {!nodesReliable ? (
        <Alert type="warning" showIcon message={t('hr.payroll.nodesUnavailable')} />
      ) : nodes.length === 0 ? (
        <Alert type="warning" showIcon message={t('hr.payroll.noCostCenters')} />
      ) : (
        <Form<FormValues>
          form={form}
          layout="vertical"
          preserve={false}
          disabled={generate.isPending}
        >
          <Form.Item
            name="orgNodeId"
            label={t('hr.payroll.orgNode')}
            extra={t('hr.payroll.orgNodeHint')}
            rules={[{ required: true, message: t('hr.payroll.validation.orgNodeRequired') }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={t('hr.payroll.orgNodePlaceholder')}
              options={nodes.map((node) => ({
                value: node.id,
                label: `${node.displayName} · ${node.code}`,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="year"
            label={t('hr.payroll.year')}
            rules={[{ required: true, message: t('hr.payroll.validation.yearRequired') }]}
          >
            <InputNumber min={2000} max={2100} precision={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="month"
            label={t('hr.payroll.month')}
            rules={[{ required: true, message: t('hr.payroll.validation.monthRequired') }]}
          >
            <InputNumber min={1} max={12} precision={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="note"
            label={t('hr.payroll.note')}
            rules={[{ max: REASON_MAX, message: t('hr.assignment.validation.reasonLength') }]}
          >
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  )
}
